import React, { useEffect, useRef, useState } from "react";
import { initCurrentTab, publishDetailsHtmlSetter, publishDetailsReloader } from "./globalBridge";

interface PatientDetailsPanelProps {
  patientId: number | null;
  urlTemplate: string;
  loadingMessage?: string;
}

function detailsUrl(urlTemplate: string, patientId: number): string {
  return urlTemplate.replace("__PATIENT_ID__", String(patientId));
}

// current_readings.html.erb ships inline (and external) <script> tags that define
// panel-scoped handlers — acknowledge_bulk_readings(), acknowledge_all_bulk_readings(),
// newNote(), the invalidate/trend dialog setup, etc. The legacy monitor injects this HTML
// with jQuery .html(), which executes those scripts; React's dangerouslySetInnerHTML does
// not, so the handlers were never defined and e.g. "Acknowledge selected readings" threw a
// ReferenceError with no popup. Re-create each <script> node so the browser runs it,
// restoring parity with the legacy dashboard (HHMCOVID-6212).
//
// This intentionally re-runs external <script src> tags too (current_readings ships
// kidget/flot/infodialog/reorderlist/ecg_reading/limits). Those libraries power the
// panel's inline-editing, trend graphs and ECG rendering and are NOT loaded by the
// monitor host (encounter_monitor.js) — only by this panel — so we must not filter them
// out. Legacy's jQuery .html() executes them on every panel load in exactly the same way;
// the browser serves the static files from cache, so the cost is re-running their setup.
function runInjectedScripts(container: HTMLElement | null): void {
  if (!container) {
    return;
  }
  Array.from(container.querySelectorAll("script")).forEach((oldScript) => {
    const newScript = document.createElement("script");
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.text = oldScript.textContent || "";
    if (oldScript.parentNode) {
      oldScript.parentNode.replaceChild(newScript, oldScript);
    }
  });
}

// current_readings.html.erb tags #current-readings-content with data-patient-id, so a
// pushed HTML payload can be attributed to a patient. Parsing via innerHTML does NOT execute
// scripts (that only happens through runInjectedScripts on the real render), so this is a
// safe read-only extraction (HHMCOVID-6234, PR #414 review).
function patientIdFromHtml(html: string): number | null {
  if (!html) {
    return null;
  }
  const container = document.createElement("div");
  container.innerHTML = html;
  const node = container.querySelector("#current-readings-content[data-patient-id]");
  const raw = node ? node.getAttribute("data-patient-id") : null;
  const id = raw != null ? Number(raw) : NaN;
  return Number.isFinite(id) && id > 0 ? id : null;
}

export default function PatientDetailsPanel(props: PatientDetailsPanelProps) {
  const { patientId, urlTemplate, loadingMessage } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [html, setHtml] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  // Track the open patient and a monotonic write sequence so a late async response can't
  // clobber fresher content (HHMCOVID-6234, PR #414 review).
  const patientIdRef = useRef<number | null>(patientId);
  const writeSeqRef = useRef(0);

  useEffect(() => {
    patientIdRef.current = patientId;
  }, [patientId]);

  useEffect(() => {
    // Legacy dialogs that return refreshed current_readings HTML (hold, acknowledge) push it
    // into React state here, instead of mutating the React-owned #patient-details node, which
    // would crash the next render (HHMCOVID-6234).
    publishDetailsHtmlSetter((incoming: string) => {
      // Drop a payload whose patient no longer matches the open panel — e.g. the user
      // switched patients while the dialog's request was in flight (last-action-wins).
      const targetId = patientIdFromHtml(incoming);
      if (targetId != null && targetId !== patientIdRef.current) {
        return;
      }
      // Claim the newest write so an older in-flight fetch can't overwrite this content.
      writeSeqRef.current += 1;
      setError("");
      setLoading(false);
      setHtml(incoming || "");
    });
    // Legacy dialogs that don't return HTML (clear-hold, clear-expired, override) refresh the
    // panel by forcing a re-fetch of the open patient through the normal fetch path.
    publishDetailsReloader(() => setReloadNonce((n) => n + 1));
  }, []);

  useEffect(() => {
    if (!patientId || !urlTemplate) {
      setLoading(false);
      setError("");
      setHtml("");
      return;
    }

    let cancelled = false;
    // Claim a write generation; a dialog-pushed update (setter) or a newer fetch bumps this,
    // and this fetch then skips its own response so it can't clobber fresher content.
    const fetchSeq = (writeSeqRef.current += 1);
    const isCurrent = () => !cancelled && writeSeqRef.current === fetchSeq;
    setLoading(true);
    setError("");

    const url = detailsUrl(urlTemplate, patientId);
    const jq = (window as any).$j || (window as any).jQuery;
    const kanayoGlobals = (window as any).kanayo_globals;

    if (jq && typeof jq.kanayoAjax === "function") {
      jq.kanayoAjax({
        url: url,
        type: "GET",
        dataType: "text",
        kanayoProgressError: kanayoGlobals?.progress?.error, // must be truthy or stopProgress() never fires on failure (jquery.ajax.js:25-27)
        success: (data: string) => {
          if (isCurrent()) {
            setHtml(data || "");
            setLoading(false);
          }
        },
        error: (jqXHR: any, _textStatus: string, errorThrown: string) => {
          if (isCurrent()) {
            const status = jqXHR && jqXHR.status ? ` (status ${jqXHR.status})` : "";
            setError(`Failed to load patient details${status}: ${errorThrown || "request failed"}`);
            setLoading(false);
          }
        }
      });
    } else {
      fetch(url, { method: "GET", credentials: "same-origin", headers: { Accept: "text/html" } })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Current readings request failed with status ${response.status}`);
          }
          return response.text();
        })
        .then((body) => {
          if (isCurrent()) {
            setHtml(body || "");
          }
        })
        .catch((err) => {
          if (isCurrent()) {
            setError(`${err instanceof Error ? err.message : "Failed to load current readings"} (url: ${url})`);
          }
        })
        .finally(() => {
          if (isCurrent()) {
            setLoading(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [patientId, urlTemplate, reloadNonce]);

  useEffect(() => {
    if (!html) {
      return;
    }
    const handle = requestAnimationFrame(() => {
      // Run the panel's inline scripts first (defines its onclick handlers), then
      // re-init the collapsible reading sections — same order the legacy monitor uses.
      runInjectedScripts(contentRef.current);
      initCurrentTab();
    });
    return () => cancelAnimationFrame(handle);
  }, [html]);

  return (
    <div id="patient-details" className="monitor-react-patient-details">
      {patientId == null && <div className="monitor-react-patient-details-empty" />}
      {patientId != null && loading && (
        <div className="monitor-react-patient-details-loading">{loadingMessage || "Loading details..."}</div>
      )}
      {patientId != null && error.length > 0 && <div className="alert alert-danger py-2">{error}</div>}
      {patientId != null && !loading && error.length === 0 && (
        <div ref={contentRef} className="monitor-react-patient-details-content" dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </div>
  );
}
